import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  Sun, CloudRain, Wind, Drop, ArrowRight, Newspaper, Package, Basket,
  Warning, Sparkle, MapPin, Plant, Clock, Calendar,
} from "@phosphor-icons/react";
import FarmersNav from "@/components/FarmersNav";
import { useAuth } from "@/contexts/AuthContext";

/**
 * FarmersToday — the farmer's daily-driver landing page.
 *
 * Weather · inventory alerts · reminders · upcoming harvests · fresh
 * products · nearby reports · latest news — all on one screen.
 *
 * Requests the browser location, then pipes it into GET /api/farmers/today.
 * Falls back to a saved-location cache (localStorage np:farmers-location)
 * when the user refuses geolocation.
 */
const LOCAL_KEY = "np:farmers-location";

const WEATHER_LABEL = {
  0: "Clear",  1: "Mostly clear", 2: "Partly cloudy", 3: "Overcast",
  45: "Fog",   48: "Rime fog",
  51: "Light drizzle", 61: "Light rain", 63: "Moderate rain", 65: "Heavy rain",
  71: "Light snow", 73: "Moderate snow", 75: "Heavy snow",
  95: "Thunderstorm", 96: "Storm w/ hail",
};

export default function FarmersToday() {
  const { user } = useAuth();
  const [loc, setLoc] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LOCAL_KEY) || "null"); }
    catch { return null; }
  });
  const [d, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (loc) return; // already have one
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const next = { lat: p.coords.latitude, lon: p.coords.longitude };
        localStorage.setItem(LOCAL_KEY, JSON.stringify(next));
        setLoc(next);
      },
      () => { /* denied — proceed w/o coords, backend still returns news */ },
      { timeout: 6000 },
    );
  }, [loc]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (loc?.lat != null) { params.set("latitude", loc.lat); params.set("longitude", loc.lon); }
    http.get(`/farmers/today?${params}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Couldn't load today's dashboard"))
      .finally(() => setLoading(false));
  }, [loc]);

  const greeting = greetingByHour();
  const name = user?.name?.split(" ")[0];

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 px-5 md:px-10 bg-[#F5EBDC]" data-testid="farmers-today-page">
      <div className="max-w-6xl mx-auto"><FarmersNav /></div>

      <div className="max-w-6xl mx-auto mt-6">
        <header>
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            <Sparkle size={12} weight="fill" className="inline mr-1" /> Today
          </div>
          <h1 className="mt-1 font-serif-display text-4xl md:text-5xl leading-[1.02] tracking-tight">
            {name ? `${greeting}, ${name}.` : `${greeting}.`}
          </h1>
          <p className="mt-2 text-[#3A3830]">
            Weather, farm alerts, harvests due, fresh listings and news that matter to your land.
          </p>
          {!loc && (
            <button
              onClick={requestLocation}
              data-testid="farmers-today-locate"
              className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[color:var(--np-brand,#B85F2A)]/40 bg-white px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:bg-[color:var(--np-brand,#B85F2A)] hover:text-white"
            >
              <MapPin size={11} weight="fill" /> Use my location
            </button>
          )}
        </header>

        {/* Weather tiles */}
        <section className="mt-8" data-testid="farmers-today-weather">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Weather</div>
          {loading && !d ? (
            <div className="mt-2 text-[#3A3830]">Loading…</div>
          ) : d?.weather ? (
            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3">
              <Tile icon={Sun}
                    label={WEATHER_LABEL[d.weather.weather_code] || "Now"}
                    value={`${Math.round(d.weather.temperature_c ?? 0)}°C`}
                    sub={`Feels ${Math.round(d.weather.today_high_c ?? 0)}° / ${Math.round(d.weather.today_low_c ?? 0)}°`} />
              <Tile icon={CloudRain} label="Rain today"
                    value={`${(d.weather.today_rain_mm ?? 0).toFixed(1)} mm`}
                    sub={d.weather.precipitation_mm > 0 ? "Falling now" : "Dry"} />
              <Tile icon={Wind}  label="Wind"      value={`${Math.round(d.weather.wind_kmh ?? 0)} kmh`}
                    sub="Right now" />
              <Tile icon={Drop}  label="Humidity"  value={`${d.weather.humidity ?? 0}%`}
                    sub={fmtSun(d.weather.sunrise, d.weather.sunset)} />
            </div>
          ) : (
            <p className="mt-2 text-[#3A3830]">Share your location above to see weather.</p>
          )}
        </section>

        {/* Inventory + harvests + fresh products (signed-in only) */}
        {user && d && (
          <section className="mt-10 grid md:grid-cols-3 gap-3" data-testid="farmers-today-farm">
            <FarmCard title="Inventory attention" icon={Package} to="/farmers/inventory"
                      empty="No alerts — nice work">
              {d.inventory && (d.inventory.expiring + d.inventory.expired + d.inventory.critical + d.inventory.low > 0) ? (
                <ul className="mt-2 text-sm text-[#3A3830] space-y-1">
                  {d.inventory.expired  > 0 && <li>· {d.inventory.expired} expired</li>}
                  {d.inventory.expiring > 0 && <li>· {d.inventory.expiring} expiring within 7d</li>}
                  {d.inventory.critical > 0 && <li>· {d.inventory.critical} critical stock</li>}
                  {d.inventory.low      > 0 && <li>· {d.inventory.low} low stock</li>}
                  {d.inventory.reminders_due > 0 && <li>· {d.inventory.reminders_due} reminders due</li>}
                </ul>
              ) : null}
            </FarmCard>

            <FarmCard title="Upcoming harvests" icon={Calendar} to="/farmers/mine?tab=harvests"
                      empty="No harvests scheduled">
              {d.upcoming_harvests?.length ? (
                <ul className="mt-2 text-sm text-[#3A3830] space-y-1">
                  {d.upcoming_harvests.slice(0, 5).map((h) => (
                    <li key={h.id}>· {h.crop} — {h.harvest_date}{h.quantity ? ` (${h.quantity} ${h.unit || ""})` : ""}</li>
                  ))}
                </ul>
              ) : null}
            </FarmCard>

            <FarmCard title="Fresh today" icon={Basket} to="/farmers/websites"
                      empty="No fresh products listed">
              {d.fresh_products?.length ? (
                <ul className="mt-2 text-sm text-[#3A3830] space-y-1">
                  {d.fresh_products.slice(0, 5).map((p) => (
                    <li key={p.id}>· {p.name}{p.price ? ` — ${p.currency || "$"}${p.price}/${p.unit || "ea"}` : ""}</li>
                  ))}
                </ul>
              ) : null}
            </FarmCard>
          </section>
        )}

        {/* Nearby reports */}
        {d?.nearby_reports?.length > 0 && (
          <section className="mt-10" data-testid="farmers-today-nearby">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Nearby · last 3 days</div>
            <h2 className="mt-1 font-serif-display text-2xl">What your neighbours are reporting</h2>
            <div className="mt-3 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {d.nearby_reports.map((r) => (
                <div key={r.id} className="rounded-2xl border border-black/10 bg-white p-4">
                  <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                    {r.category} {r.severity && r.severity !== "unknown" ? `· ${r.severity}` : ""}
                  </div>
                  <div className="mt-1 font-serif-display text-lg">{r.title || "(no title)"}</div>
                  {r.location?.city && (
                    <div className="mt-1 text-xs text-[#3A3830]">
                      <MapPin size={10} weight="fill" className="inline mr-1" /> {r.location.city}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* News */}
        {d?.news?.length > 0 && (
          <section className="mt-12" data-testid="farmers-today-news">
            <div className="flex items-end justify-between flex-wrap gap-2">
              <div>
                <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Farm news</div>
                <h2 className="mt-1 font-serif-display text-2xl">Latest across agriculture, weather & water</h2>
              </div>
              <Link to="/farmers/news"
                    data-testid="farmers-today-news-all"
                    className="rounded-full border border-black/15 bg-white px-3 py-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:border-[color:var(--np-brand,#B85F2A)] hover:text-[color:var(--np-brand,#B85F2A)]">
                All news <ArrowRight size={10} weight="bold" className="inline ml-1" />
              </Link>
            </div>
            <div className="mt-3 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {d.news.map((n) => (
                <a key={n.id} href={n.url} target="_blank" rel="noopener noreferrer"
                   data-testid={`farmers-today-news-${n.id}`}
                   className="block rounded-2xl border border-black/10 bg-white p-4 hover:border-[color:var(--np-brand,#B85F2A)] hover:shadow-sm transition">
                  <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]">
                    {n.publisher || n.source_domain}
                  </div>
                  <div className="mt-1 font-serif-display text-base leading-tight">{n.headline || n.title}</div>
                  {n.summary && <p className="mt-1 text-sm text-[#3A3830] line-clamp-2">{n.summary}</p>}
                  <div className="mt-2 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                    Read <ArrowRight size={10} weight="bold" />
                  </div>
                </a>
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}

function requestLocation() {
  if (!navigator.geolocation) return;
  navigator.geolocation.getCurrentPosition(
    (p) => {
      localStorage.setItem("np:farmers-location", JSON.stringify({ lat: p.coords.latitude, lon: p.coords.longitude }));
      window.location.reload();
    },
    () => toast.error("Location permission declined"),
  );
}

function Tile({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-4">
      <div className="flex items-center gap-2 text-[color:var(--np-brand,#B85F2A)]">
        <Icon size={14} weight="fill" />
        <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]">{label}</div>
      </div>
      <div className="mt-1 font-serif-display text-2xl md:text-3xl">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-[#3A3830]">{sub}</div>}
    </div>
  );
}

function FarmCard({ title, icon: Icon, to, empty, children }) {
  const hasContent = !!children && children?.props?.children;
  return (
    <Link to={to} className="rounded-2xl border border-black/10 bg-white p-4 hover:border-[color:var(--np-brand,#B85F2A)] hover:shadow-sm transition block">
      <div className="flex items-center gap-2 text-[color:var(--np-brand,#B85F2A)]">
        <Icon size={14} weight="fill" />
        <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]">{title}</div>
      </div>
      {hasContent ? children : (
        <div className="mt-2 text-sm text-[#3A3830]">{empty}</div>
      )}
      <div className="mt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
        Open <ArrowRight size={10} weight="bold" />
      </div>
    </Link>
  );
}

function greetingByHour() {
  const h = new Date().getHours();
  if (h < 5)  return "Late night";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 21) return "Good evening";
  return "Late evening";
}

function fmtSun(sunrise, sunset) {
  if (!sunrise || !sunset) return "";
  const rise = new Date(sunrise).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const set  = new Date(sunset).toLocaleTimeString([],  { hour: "numeric", minute: "2-digit" });
  return `☀ ${rise} · ${set}`;
}
